<template>
  <div class="sms-campaign-list">
    <template v-if="isHistoryList">
      <vs-tabs
        position="top"
        v-model="tabActive"
        class="tabs-shadow-none">

        <vs-tab
          v-for="(tab, index) in tabs"
          :key="index"
          icon-pack="feather"
          :icon="tab.icon"
          :label="tab.label"
          class="p-0"
        >
        </vs-tab>
      </vs-tabs>

      <vs-divider/>
    </template>

    <div class="vx-card p-6">
      <base-ag-grid-header
        :rows-selected-count="rowsSelectedCountWithAllSelection"
        :filters-count="filtersAppliedCount"
        :filters-match.sync="filtersMatch"
        @reset-selection="setSelectedRowsNone()"
        @reset-filters="resetFilters()">
        <template v-slot:header-left>
          <items-per-page
            :count="count"
            :currentPage="currentPage"
            :paginationPageSize="paginationPageSize"
            @changed-page-size="paginationSetPageSize">
          </items-per-page>

          <list-action-drop-down
            v-if="isAnyRowsSelected"
            :options="campaignMultipleActionOptions"
            class="ml-3"
            @export="exportData()"
            @delete="confirmDeleteCampaigns()">
          </list-action-drop-down>
        </template>

        <template v-slot:header-right>
          <page-header-actions
            class="hidden md:flex"
            :actions="headerActions"
            @action="onHeaderAction"
          />
        </template>
      </base-ag-grid-header>

      <selection-alert-info
        v-model="selectionPageAlert"
        class="mb-5"
        :entity="entity"
        :count="count"
        :rows-selected-count="rowsSelectedCount"
        @select-all="setSelectedRowsAll()"></selection-alert-info>

      <export-alert-info
        :entity="$tc(`$Entities.${entity}`, 2)"
        :exporting.sync="exportingList"
        :exported.sync="exportedList"
        :fileUrl="urlFileExported"
        :send-email="exportSendEmail"/>

      <ag-grid-vue
        :key="agGridKey"
        ref="agGridTable"
        :domLayout="domLayout"
        :style="gridStyle"
        :components="components"
        :frameworkComponents="frameworkComponents"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :column-types="columnTypes"
        :getRowNodeId="getRowNodeId"
        :autoParamsRefresh="true"
        rowSelection="multiple"
        :animateRows="true"
        :suppressRowClickSelection="true"
        rowModelType="infinite"
        :pagination="hasPagination"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="suppressPaginationPanel"
        :overlayLoadingTemplate="overlayLoadingTemplate"
        :noRowsOverlayComponent="noRowsOverlayComponent"
        :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :cacheBlockSize="cacheBlockSize"
        :maxBlocksInCache="maxBlocksInCache"
        @selection-changed="onSelectionChanged"
        @grid-ready="onGridReady"
        @model-updated="onModelUpdate"
        @grid-size-changed="onGridSizeChanged"
        @first-data-rendered="onFirstDataRendered"
        @row-clicked="onRowClicked"
        @row-double-clicked="onRowDoubleClicked"
        @filter-changed="onFilterChanged"
        @pagination-changed="onPaginationChanged">
      </ag-grid-vue>

      <!-- Mobile Sticky Bottom Bar -->
      <div
        class="sticky-bottom-bar-icons md:hidden"
        :style="{ gridTemplateColumns: `repeat(${(isAnyRowsSelected ? 1 : 0) + 1}, 1fr)` }"
      >
        <vs-dropdown
          v-if="isAnyRowsSelected"
          vs-custom-content
          vs-trigger-click
          class="mobile-bar-btn mobile-bar-btn-simple"
        >
          <div class="mobile-bar-btn-content">
            <i class="feather icon-more-horizontal mobile-bar-icon"></i>
            <span class="mobile-btn-label">Actions</span>
          </div>
          <vs-dropdown-menu class="mobile-actions-dropdown gp-sticky-dropdown-menu">
            <ul class="mobile-actions-list">
              <li
                v-for="(opt, idx) in mobileStickyBulkActionOptions"
                :key="`mba-${idx}`"
                class="mobile-actions-item"
                :class="{ disabled: !!opt.disabled }"
                @click="opt.disabled ? null : onMobileBulkAction(opt.event)"
              >
                <span class="flex items-center">
                  <feather-icon :icon="opt.icon" svgClasses="h-4 w-4" class="mr-2" />
                  <span>{{ opt.label }}</span>
                </span>
              </li>
            </ul>
          </vs-dropdown-menu>
        </vs-dropdown>

        <button
          class="mobile-bar-btn mobile-bar-btn-simple mobile-bar-btn-refresh"
          type="button"
          @click="resetDataSourceAndSelection()"
        >
          <i class="feather icon-refresh-cw mobile-bar-icon"></i>
          <span class="mobile-btn-label">Refresh</span>
        </button>
      </div>

      <vs-popup
        :title="exportModalTitle"
        :active.sync="activeModalToExport">
        <transition name="zoom-fade">
          <export-json-to-excel
            v-if="showExportComponent"
            :columns="getColumnsToExport"
            :data="rowsSelected"
            @close="activeModalToExport=false"
            @export="onExport">
          </export-json-to-excel>
        </transition>
      </vs-popup>

      <vs-popup
        fullscreen
        :title="$t('$CampaignsModules.DetailCampaignTitle')"
        :active.sync="activeModalView">
        <transition name="zoom-fade">
          <campaign-specified-chart
            v-if="showCreateOrEditComponent"
            :entity="entity"
            :campaign-type="$enums.Campaign.Type.SMS"
            :fetch-function="fetchSMSCampaign"
            :fetch-details-function="fetchSMSCampaignDetails"
            :model-payload="recordSelected"/>
        </transition>
      </vs-popup>
    </div>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import {
  mapActions, mapMutations, mapState,
} from 'vuex';
import { registerSocketEvent, unregisterSocketEvent } from '@/socket';

// mixins
import campaignListWithAgGrid from '@/views/modules/campaigns/common/mixins/campaignListWithAgGrid';

// Custom Components
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import ItemsPerPage from '@/views/modules/_components/ItemsPerPage.vue';
import ExportAlertInfo from '@/views/modules/_components/ExportAlertInfo.vue';
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import ExportJsonToExcel from '@/views/modules/_components/ExportJsonToExcel.vue';
import ListActionDropDown from '@/views/modules/_components/ListActionDropDown.vue';
import SelectionAlertInfo from '@/views/modules/SelectionAlertInfo.vue';
import PageHeaderActions from '@/views/modules/_components/PageHeaderActions.vue';

// Cell Renderer
import CellRendererActions from '@/views/modules/campaigns/components/cell-renderers/CampaignCellRenderActions.vue';
import CellRendererStatus from '@/views/modules/campaigns/components/cell-renderers/CampaignCellRendererStatus.vue';
import CellRendererLargeText from '@/views/modules/_components/cell-renderer/CellRendererLargeTextModal.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';

/**
 * Component to list and manage SMS Campaigns
 *
 * @module views/modules/campaigns/sms-campaign/SMSCampaignList
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'SMSCampaignList',
  i18n: {
    messages: {
      en: {
        ThereAreNoSMSCampaigns: 'No SMS campaigns to display',
        $SMSCampaignCompletedNtf: {
          Title: 'SMS campaign completed',
          Text: 'The SMS campaign {name} has been completed.',
        },
        $NoRowsOverlay: {
          Title: 'No SMS campaigns in {type} to display',
          Text: 'There are no campaigns to view right now. Create a new campaign to get started.',
          Button: 'Create new campaign',
        },
        $Tabs: {
          Campaigns: 'Campaigns',
          WaitingList: 'Waitlist',
        },
      },
    },
  },
  components: {
    BaseAgGridHeader,
    ItemsPerPage,
    ListActionDropDown,
    ExportJsonToExcel,
    ExportAlertInfo,
    SelectionAlertInfo,
    PageHeaderActions,
    // eslint-disable-next-line vue/no-unused-components
    AgGridSelectionHeader,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererStatus,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLargeText,
  },
  mixins: [campaignListWithAgGrid],
  props: {
    actionsMinWidth: {
      type: Number,
      required: false,
      default: 50,
    },
  },
  data() {
    return {
      entity: this.$enums.Entity.SMS_CAMPAIGN,
      exportCollectionFunction: this.exportSMSCampaigns,
      deleteRecordFunction: this.removeSMSCampaign,
      deleteRecordsFunction: this.removeSMSCampaigns,
      resendDisabled: false,
      preferFitColumns: true,
      noRowsMessage: this.$t('ThereAreNoSMSCampaigns'),

      tabActive: 0,
      tabs: {
        campaigns: {
          icon: 'icon-message-circle',
          iconPack: 'feather',
          label: this.$t('$Tabs.Campaigns'),
        },
        waitingList: {
          icon: 'icon-users',
          iconPack: 'feather',
          label: this.$t('$Tabs.WaitingList'),
        },
      },

      columnDefs: [
        {
          colId: 'name',
          headerName: this.$t('$General.Name'),
          field: 'name',
          filter: 'agTextColumnFilter',
          minWidth: 250,
          maxWidth: 450,
          checkboxSelection: true,
          headerComponentParams: { checkboxSelection: true },
        },
        {
          colId: 'message',
          headerName: this.$tc('$General.Message'),
          field: 'message',
          filter: 'agTextColumnFilter',
          minWidth: 250,
          maxWidth: 1906,
          cellRendererFramework: 'CellRendererLargeText',
        },
        {
          colId: 'status',
          headerName: this.$t('$General.Status'),
          headerClass: 'center-herder-ag-grid',
          field: 'status',
          filter: false,
          minWidth: 150,
          maxWidth: 250,
          cellRendererFramework: 'CellRendererStatus',
        },
      ],
      components: {
        CellRendererActions,
        CellRendererStatus,
        CellRendererLargeText,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },

      socketEventsListenerId: 'sms-campaign-list',
    };
  },
  computed: {
    ...mapState({
      draftCampaign: (state) => state.smsCampaign.draftCampaign,
    }),
    additionalActionsOnList() {
      return [{
        name: this.$enums.Operation.RESEND,
        color: 'primary',
        text: '$General.Resend',
        position: 'top',
        icon: 'icon-send',
        iconVue: 'SendIcon',
        activeCreateOrEdit: false,
        active: !this.resendDisabled,
      }];
    },
    isHistoryList() {
      return this.listType === this.$enums.Campaign.ListType.History;
    },
    mobileStickyBulkActionOptions() {
      const options = [...this.campaignMultipleActionOptions];
      const canEdit = !this.isHistoryList;

      if (canEdit) {
        options.unshift({
          label: this.$t('$General.Edit'),
          event: this.$enums.Operation.EDIT,
          icon: 'Edit2Icon',
          disabled: this.selectedAllRows || this.rowsSelectedCount !== 1,
        });
      }

      return options;
    },
    headerActions() {
      return [
        {
          id: 'refresh',
          tooltip: 'Refresh',
          kind: 'icon',
          icon: 'icon-refresh-cw',
        },
      ];
    },
  },
  watch: {
    tabActive() {
      this.resetDataSourceAndSelection();
    },
    async activeModalCreateOrEdit(val) {
      if (val) {
        switch (this.operation) {
          case this.$enums.Operation.CREATE:
            this.showCreateOrEditComponent = false;
            await this.$router.push('/sms/campaigns/create');
            break;

          case this.$enums.Operation.RESEND:
            this.showCreateOrEditComponent = false;
            this.setCampaignPayload(this.recordSelected);

            if (this.recordSelected.isQuick) {
              await this.$router.push('/sms/campaigns/quick/resend');
            } else {
              await this.$router.push('/sms/campaigns/resend');
            }
            break;

          case this.$enums.Operation.EDIT:
            this.showCreateOrEditComponent = false;
            this.setCampaignPayload(this.recordSelected);
            await this.$router.push('/sms/campaigns/edit');
            break;

          case this.$enums.Operation.VIEW:
            this.activeModalView = true;
            break;

          default:
        }
      }
    },
    activeModalView(val) {
      if (!val) {
        this.activeModalCreateOrEdit = false;
      }
    },
  },
  beforeMount() {
    const $this = this;
    this.noRowsOverlayComponent = 'customNoRowsOverlay';
    this.noRowsOverlayComponentParams = {
      title: this.$t('$NoRowsOverlay.Title', {
        type: this.$t(`$Enums.Campaign.ListType.${this.listType}`).toLowerCase(),
      }),
      text: this.$t('$NoRowsOverlay.Text'),
      buttonText: this.$t('$NoRowsOverlay.Button'),
      async actionButtonClicked() {
        await $this.$router.push({
          name: 'sms-campaigns-create',
        });
      },
    };
  },
  mounted() {
    registerSocketEvent(this.$enums.SocketEvents.SMS_CAMPAIGN_UPDATED, (smsCampaign) => {
      if (smsCampaign.status === this.$enums.Campaign.Status.COMPLETED) {
        this.showCampaignCompletedNft(smsCampaign.name);
      }

      this.refreshRowNodeDataById(smsCampaign.id, smsCampaign);
    },
    this.socketEventsListenerId,
    true);
  },
  beforeDestroy() {
    unregisterSocketEvent(
      this.$enums.SocketEvents.SMS_CAMPAIGN_UPDATED,
      this.socketEventsListenerId,
    );
  },
  methods: {
    onHeaderAction({ id }) {
      switch (id) {
        case 'refresh':
          this.resetDataSourceAndSelection();
          break;
        default:
          break;
      }
    },
    onMobileBulkAction(event) {
      switch (event) {
        case this.$enums.Operation.EDIT:
          if (this.rowsSelectedCount === 1 && this.rowsSelected && this.rowsSelected[0]) {
            this.editRecord(this.rowsSelected[0]);
          }
          break;
        case 'export':
          this.exportData();
          break;
        case 'delete':
          this.confirmDeleteCampaigns();
          break;
        default:
          break;
      }
    },
    ...mapActions({
      fetchAllSMSCampaigns: 'smsCampaign/fetchAllSMSCampaigns',
      fetchSMSCampaign: 'smsCampaign/fetchSMSCampaign',
      fetchSMSCampaignDetails: 'smsCampaign/fetchSMSCampaignDetails',
      exportSMSCampaigns: 'smsCampaign/exportFile',
      removeSMSCampaign: 'smsCampaign/removeSMSCampaign',
      removeSMSCampaigns: 'smsCampaign/removeSMSCampaigns',
    }),
    ...mapMutations({
      setCampaignPayload: 'smsCampaign/SET_SMS_CAMPAIGN_PAYLOAD',
    }),
    async fetchAgGridData(params) {
      let filters = {
        ...params.filters,
      };
      let { filtersMatch } = params;

      if (this.statusFilter || this.isHistoryList) {
        filters = {
          agGridFilters: {
            filterType: this.$enums.AppFilterType.CONDITIONAL,
            filtersMatch: params.filtersMatch,
            filter: params.filters,
          },
        };

        if (this.statusFilter) {
          filters = {
            ...filters,
            status: this.statusFilter,
          };
        }

        if (this.isHistoryList) {
          filters = {
            ...filters,
            isWaitingList: {
              filterType: this.$enums.AppFilterType.BOOLEAN,
              filter: this.tabActive === 0
                ? this.$enums.AppFilter.FilterType.Boolean.Values.NO
                : this.$enums.AppFilter.FilterType.Boolean.Values.YES,
            },
          };
        }

        filtersMatch = this.$enums.AppFilterMathType.ALL;
      }

      return this.fetchAllSMSCampaigns({
        ...params,
        filters,
        filtersMatch,
        populate: [
          { path: 'senderId', select: 'id dialCode phoneSignificant name' },
        ],
      });
    },
    showCampaignCompletedNft(name) {
      this.$showSuccessActionNotification({
        title: this.$t('$SMSCampaignCompletedNtf.Title'),
        text: this.$t('$SMSCampaignCompletedNtf.Text', { name }),
      });
    },
    resendRecord(recordSelected = null) {
      this.operation = this.$enums.Operation.RESEND;
      this.recordSelected = recordSelected;
      this.activeModalCreateOrEdit = true;
    },
  },
};
</script>

<style scoped lang="scss">
// Mobile Sticky Bottom Bar (icon buttons)
.sticky-bottom-bar-icons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E5E7EB;
  padding: 8px 12px;
  display: grid;
  gap: 8px;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin: 0 -1.5rem -1.5rem; // match vx-card p-6

  @media (max-width: 767px) {
    position: -webkit-sticky;
    position: sticky;
  }
}

.mobile-bar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  color: #6B7280;
  font-family: inherit;
  outline: none;

  &:active {
    transform: scale(0.95);
    background: rgba(139, 92, 246, 0.1);
  }
}

.mobile-bar-btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.mobile-bar-icon {
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
  width: 22px;
  height: 22px;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

.mobile-bar-btn-simple {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }
}

.mobile-bar-btn-refresh {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }

  &:not(:active):not(:hover) {
    background: transparent !important;
  }
}

.mobile-actions-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-actions-item {
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(139, 92, 246, 0.06);
  }
}
</style>
